import { useEffect, useState } from 'react';
import { EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useGuestSessionState } from '@shared/hooks/useGuestSessionState';
import { useTimeout } from '@shared/hooks/useTimeout';

export type UseRsvpAttendanceStatusArgs = Readonly<{
  eventHandle: string | undefined | null;
  skip?: boolean;
}>;

export type UseRsvpAttendanceStatusReturn = Readonly<{
  /**
   * The viewing guest's RSVP attendance status for the event, or `undefined` while the
   * session query is still resolving (or when it fails). Callers that gate content should
   * treat `undefined` as "not yet known" — never as a decline.
   */
  rsvpAttendanceStatus: EventRSVPAttendanceStatus | undefined;
  /**
   * True once the query has settled (or was skipped). Gate "attending-only" content on
   * `isResolved && !isNotAttending` so a declining guest never sees the content flash in
   * before their status arrives.
   */
  isResolved: boolean;
  /** True only when the guest has RSVP'd attending. */
  isAttending: boolean;
  /**
   * True only when the guest has EXPLICITLY RSVP'd not attending. Gate "attending-only"
   * content (hotel upsells, stay reminders) on this: hide it for notAttending guests, but
   * keep showing it to guests whose status is unknown or who haven't RSVP'd yet.
   */
  isNotAttending: boolean;
}>;

/**
 * How long a caller waits on the status before being told to carry on without it.
 *
 * `eventCTA` proxies the legacy Service API (~750ms), and every caller gates
 * attending-only content on `isResolved`. Without a ceiling, one slow or hung read
 * suppresses that content indefinitely — for the whole audience, not just decliners.
 * Reporting "resolved, status unknown" degrades to showing it, which is the safe side.
 */
const STATUS_WAIT_CEILING_MS = 2000;

/**
 * Reads the viewing guest's RSVP attendance status for the event.
 *
 * Shares one session read with `useGuestSessionIdentity` (see `useGuestSessionState`),
 * which owns the fetch policy and session forwarding. This hook adds the resolution rules
 * that gating on attendance needs and identity does not.
 */
export const useRsvpAttendanceStatus = ({ eventHandle, skip }: UseRsvpAttendanceStatusArgs): UseRsvpAttendanceStatusReturn => {
  const { rsvpAttendanceStatus, loading, isSkipped } = useGuestSessionState({ eventHandle, skip });

  // Only a real yes/no counts as an answer worth acting on early. `cache-and-network` hands
  // back the cached payload first, and that cache may hold the anonymous session this hook
  // exists to revalidate — which reads as `unanswered`. Resolving on mere delivery would
  // let a declining guest see the CTA until the network leg lands, the exact flash the
  // `isResolved` gate is for. A definitive cached answer is safe to trust immediately.
  const isDefinitivelyAnswered = rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending || rsvpAttendanceStatus === EventRSVPAttendanceStatus.notAttending;

  const [hasWaitedLongEnough, setHasWaitedLongEnough] = useState(false);
  const isStillWaiting = !isSkipped && !isDefinitivelyAnswered && loading;
  const { set: setWaitTimeout, clear: clearWaitTimeout } = useTimeout();

  useEffect(() => {
    if (isStillWaiting) {
      setWaitTimeout(() => setHasWaitedLongEnough(true), STATUS_WAIT_CEILING_MS);
    } else {
      clearWaitTimeout();
    }
  }, [isStillWaiting, setWaitTimeout, clearWaitTimeout]);

  return {
    rsvpAttendanceStatus,
    isResolved: isSkipped || isDefinitivelyAnswered || !loading || hasWaitedLongEnough,
    isAttending: rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending,
    isNotAttending: rsvpAttendanceStatus === EventRSVPAttendanceStatus.notAttending,
  };
};
